import { createLogger } from "../../../../utils/logger";

const logger = createLogger("Auth");

/**
 * Login user by setting authentication cookie
 * @param token - JWT bearer token
 * @returns Promise<{ success: boolean, publisherId?: string, error?: string }>
 */
export const login = async (
    token: string,
): Promise<{ success: boolean; publisherId?: string; error?: string }> => {
    try {
        const response = await fetch("/api/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
            credentials: "include",
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                error: data.error || "Login failed",
            };
        }

        return {
            success: true,
            publisherId: data.publisherId,
        };
    } catch (error) {
        logger.error("Login failed:", error);
        return {
            success: false,
            error: error instanceof Error ? error.message : "Login failed",
        };
    }
};

/**
 * Logout user by clearing authentication cookie
 */
export const logout = async (): Promise<void> => {
    try {
        await fetch("/api/auth/logout", {
            method: "POST",
            credentials: "include",
        });
    } catch (error) {
        logger.error("Logout failed:", error);
    }
};

/**
 * Check if user is authenticated by checking cookie
 * @returns boolean - true if token_set cookie exists
 */
export const isAuthenticated = (): boolean => {
    return document.cookie.includes("token_set=true");
};

/**
 * Get publisher ID from cookie
 * @returns string | null
 */
export const getPublisherId = (): string | null => {
    const match = document.cookie.match(/publisher_id=([^;]+)/);
    return match ? match[1] ?? null : null;
};

/**
 * Check authentication status with server
 * @returns Promise<{ authenticated: boolean, publisherId?: string }>
 */
export const checkAuthStatus = async (): Promise<{
    authenticated: boolean;
    publisherId?: string;
}> => {
    try {
        const response = await fetch("/api/auth/status", {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            return { authenticated: false };
        }

        return await response.json();
    } catch (error) {
        logger.error("Status check failed:", error);
        return { authenticated: false };
    }
};
